/**
 * Thin dataLayer helpers. Every event lands in the GTM container
 * (GTM-5NDBXQLD), where GA4 / Ads / LinkedIn tags pick it up.
 */

type DataLayerObject = Record<string, unknown>;

/** GA4 data stream for trustscale.ai (configured directly in index.html). */
export const GA4_MEASUREMENT_ID = "G-VMQPYQE6D5";

export function pushToDataLayer(payload: DataLayerObject) {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

/**
 * gtag() must receive its real `arguments` object — Google's tag ignores a
 * plain array pushed to the dataLayer, which silently breaks consent updates.
 */
export const gtag = function () {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
} as (...args: unknown[]) => void;

export type AnalyticsEvent =
  | "page_view_spa"
  | "demo_click"
  | "form_start"
  | "form_submit"
  | "add_credits_click"
  | "sign_in_click";

export function trackEvent(event: AnalyticsEvent, params: DataLayerObject = {}) {
  // GA4 is configured inside the GTM container, so the dataLayer is the only
  // channel — sending via gtag() too would double-count every event.
  pushToDataLayer({ event, ...params });
}

/** CTA click helper — `location` describes page + button. */
export function trackCta(
  event: Extract<AnalyticsEvent, "demo_click" | "add_credits_click" | "sign_in_click">,
  location: string,
  params: DataLayerObject = {},
) {
  trackEvent(event, { location, ...params });
}

export function trackFormStart(formName: string, location?: string) {
  trackEvent("form_start", { form_name: formName, location: location ?? formName });
}

export function trackFormSubmit(formName: string, location?: string) {
  trackEvent("form_submit", { form_name: formName, location: location ?? formName });
}

let isFirstPageView = true;

/** Locale prefixes served by the i18n router. */
const LOCALES = ["en", "es", "fr", "de", "it", "pt", "nl", "ja", "zh", "ar", "hi"];

/**
 * `/de/pricing` and `/en/pricing` are the same page in different languages.
 * Report one canonical path plus a `content_locale` dimension so GA4 rolls the
 * languages together instead of splitting every report 11 ways.
 */
export function normalizePath(path: string): { path: string; locale: string } {
  const [, first = ""] = path.split("/");
  if (!LOCALES.includes(first)) return { path, locale: "en" };
  return { path: path.slice(first.length + 1) || "/", locale: first };
}

export function trackPageView(path: string, title: string) {
  const { path: canonicalPath, locale } = normalizePath(path);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // GA4 derives the reported page path from page_location, so send the
  // locale-stripped URL there.
  const page_location = origin + canonicalPath;

  // The container's Google tag already sends a page_view on initial load, so
  // only route changes need an explicit signal.
  if (isFirstPageView) {
    isFirstPageView = false;
    return;
  }

  trackEvent("page_view_spa", {
    page_path: canonicalPath,
    page_title: title,
    page_location,
    content_locale: locale,
  });
}


